import { createFileRoute } from "@tanstack/react-router";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/core/Buttons/Button";
import Checkbox from "@/components/core/Checkbox";
import Loader from "@/components/core/Loaders/Loader";
import { PopUp } from "@/components/core/Modals/PopUp";
import { useSignatureSubmit } from "@/hooks/useSignatureSubmit";
import { useTagManager } from "@/hooks/useTagManager";
import { requireAuthWithDevice } from "@/lib/guards";

export const Route = createFileRoute("/$locale/$partner/signature")({
	beforeLoad: requireAuthWithDevice,
	component: Signature,
});

function Signature() {
	const { t } = useTranslation();

	useTagManager("Step Signature");

	const {
		loading,
		buttonLoader,
		showPopup,
		setShowPopup,
		checkedLegalDocs,
		legalDoc,
		error,
		finaliseSessionError,
		allLegalDocsChecked,
		legalDocs,
		handleSubmit,
		handleLegalDocDescriptionClick,
		handleCheckboxChange,
	} = useSignatureSubmit();

	if (loading) {
		return (
			<div className="absolute w-fit h-fit top-1/3 left-1/2">
				<Loader />
			</div>
		);
	}

	return (
		<>
			<div className="max-w-[81rem] mx-auto relative">
				<div className="max-w-[35rem] m-auto">
					<h1 className="text-center text-3xl font-semibold mt-16 mb-9">{t("CustomerFormPage.Accept Terms")}</h1>
					{legalDocs.map((legalDocType) => (
						<Checkbox
							key={legalDocType}
							selected={checkedLegalDocs[legalDocType] ?? false}
							onChange={(e: ChangeEvent<HTMLInputElement>) => handleCheckboxChange(legalDocType, e.target.checked)}
							description={
								<p
									className="ml-6 cursor-pointer text-[#00ADEE]"
									onClick={() => handleLegalDocDescriptionClick(legalDocType)}
									dangerouslySetInnerHTML={{
										__html: t(`Checkboxes.${legalDocType}`),
									}}
								></p>
							}
						/>
					))}
					<div className="w-fit mt-12 mx-auto">
						<Button
							label={`${t("Buttons.Complete")}`}
							loading={buttonLoader}
							disabled={!allLegalDocsChecked}
							onClick={handleSubmit}
						/>
					</div>
					{finaliseSessionError && <p className="text-center text-[#CC000B]">{t(finaliseSessionError)}</p>}
				</div>
			</div>
			<PopUp
				isOpen={showPopup}
				setIsOpen={setShowPopup}
			>
				{!legalDoc && !error && <Loader />}
				{!legalDoc && error && (
					<div className="flex items-center justify-center">
						{t("PopUp.Oops! We Can't Display this Document") ?? "Oops! We Can't Display this Document"}
					</div>
				)}
				{legalDoc && (
					<div
						dangerouslySetInnerHTML={{ __html: legalDoc }}
						onClick={(e) => e.preventDefault()}
					/>
				)}
			</PopUp>
		</>
	);
}
